<template>
  <div class="wallet-transactions">
    <spinner v-if="loading" />
    <div v-else>
      <transaction-memo
        v-if="transactionMemoModalShown"
        :memo="selectedTransaction.memo"
        :wdtransactionid="selectedTransaction.wdTransactionID"
        @close-modal="transactionMemoModalShown = false"
      />

      <div
        v-if="rows.length === 0"
        class="col-12 text-center py-3 my-2 items-not-found font-weight-normal"
      >
        No Transactions, Let's Start Now!
      </div>

      <!-- Desktop: sortable table -->
      <div v-else class="wt-table d-none d-sm-block">
        <!-- Header -->
        <div class="wt-head row item py-3 text-center font-weight-bold mx-0">
          <div class="col-3 pointer" @click="sortBy('time')">Time</div>
          <div class="col-4 pointer" @click="sortBy('type')">Type</div>
          <div class="col-3 pointer" @click="sortBy('dolar')">$</div>
          <div class="col-2">Info</div>
        </div>

        <!-- Rows: pending (actionable) + history (read-only), unified + sorted -->
        <div
          v-for="(t, i) in rows"
          :key="t._rowKey || i"
          class="col-12 my-1 px-0"
        >
          <div
            class="row item py-3 text-center mx-0 align-items-center"
            :class="{ 'wt-row--pending': t._pending }"
          >
            <div class="col-3 wt-time">
              <span class="wt-time__date">{{
                dayjs(t.createdAt).format('DD MMM YYYY')
              }}</span>
              <span class="wt-time__clock">{{
                dayjs(t.createdAt).format('hh:mm A')
              }}</span>
            </div>

            <div class="col-4 wt-type">
              <span>{{ rowLabel(t) }}</span>
              <span v-if="t._pending" class="wt-badge" :class="badgeClass(t)">
                {{ badgeText(t) }}
              </span>
            </div>

            <div class="col-3 wt-amount" :class="amountClass(t)">
              {{ rowAmount(t) }}
            </div>

            <div class="col-2 wt-info">
              <!-- Pending rows: inline actions preserved from PendingTransactions -->
              <template v-if="t._pending">
                <div class="wt-actions">
                  <button
                    v-if="t._kind === 'deposit' && !t.userMarkedAsSent"
                    class="wt-action wt-action--confirm"
                    @click="markAsSent(t._id)"
                  >
                    Sent
                  </button>
                  <button
                    class="wt-action wt-action--danger"
                    @click="
                      t._kind === 'deposit'
                        ? cancelDeposit(t._id)
                        : cancelWithdrawal(t._id)
                    "
                  >
                    Cancel
                  </button>
                </div>
              </template>
              <!-- History rows: detail icon -->
              <font-awesome-icon
                v-else
                :icon="faPlus"
                class="wt-detail-icon"
                @click="displayTransaction(t)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile: stacked cards (the 4-col table is too cramped on a phone) -->
      <div v-if="rows.length" class="wt-cards d-block d-sm-none">
        <div
          v-for="(t, i) in rows"
          :key="`m-${t._rowKey || i}`"
          class="wt-card"
          :class="{ 'wt-card--pending': t._pending }"
          @click="!t._pending && displayTransaction(t)"
        >
          <div class="wt-card__top">
            <span class="wt-card__type">{{ rowLabel(t) }}</span>
            <span class="wt-card__amount" :class="amountClass(t)">
              {{ rowAmount(t) }}
            </span>
          </div>
          <div class="wt-card__bottom">
            <span class="wt-card__time">
              {{ dayjs(t.createdAt).format('DD MMM YYYY · hh:mm A') }}
            </span>
            <span v-if="t._pending" class="wt-badge" :class="badgeClass(t)">
              {{ badgeText(t) }}
            </span>
          </div>
          <div v-if="t._pending" class="wt-card__actions">
            <button
              v-if="t._kind === 'deposit' && !t.userMarkedAsSent"
              class="wt-action wt-action--confirm"
              @click.stop="markAsSent(t._id)"
            >
              Sent
            </button>
            <button
              class="wt-action wt-action--danger"
              @click.stop="
                t._kind === 'deposit'
                  ? cancelDeposit(t._id)
                  : cancelWithdrawal(t._id)
              "
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <load-more v-if="loadMoreAvailable" @load-more="loadMoreDisplay()" />
    </div>
  </div>
</template>

<script setup>
//CORE
import dayjs from 'dayjs';
import { ref, computed, onMounted } from 'vue';
import Swal from 'sweetalert2';
//COMPONENTS
import Spinner from '@/components/global/Spinner.vue';
import LoadMore from '@/components/global/LoadMore.vue';
import transactionMemo from '@/components/specialUser/affiliates/modals/TransactionMemo.vue';
//STORE / API
import { useUserStore } from '@/stores/user';
import { useAPI } from '@/composables/useAPI';
import { PostHogUtils } from '@/core/PostHogUtils';
import {
  cancelWithdrawalRequestSuccess,
  cancelWithdrawalRequestFail,
  cancelDepositRequestSuccess,
  cancelDepositRequestFail,
  markAsSentSuccess,
  markAsSentFail,
} from '@/core/notifications';
//ICONS
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';

const userStore = useUserStore();
const { request: getTransactions } = useAPI('user/getDisplayTransactions');
const { request: cancelWithdrawalRequest } = useAPI(
  'cashier/cancelWithdrawal/',
);
const { request: markAsSentRequest } = useAPI('deposit/markAsSent/');
const { request: cancelDepositRequest } = useAPI(
  'deposit/cancelDepositRequest/',
);

const loading = ref(true);
const displayTransactions = ref([]);
const loadMoreAvailable = ref(false);
const transactionMemoModalShown = ref(false);
const selectedTransaction = ref({});
const activeSort = ref(null);
const sortDir = ref({ time: 0, type: 0, dolar: 0 });
const betPaginationCounter = ref({
  page: 1,
  limit: 25,
  numToSkip: 0,
  nextAllowed: true,
});

// Pending items, normalized into row shape with an actionable marker. Pending
// deposits + withdrawals come from the user store (same source as the old
// PendingTransactions view).
const pendingRows = computed(() => {
  const deposits = (userStore.transactions?.pendingDeposits || []).map((d) => ({
    ...d,
    _pending: true,
    _kind: 'deposit',
    _rowKey: `pd-${d._id}`,
  }));
  const withdrawals = (userStore.transactions?.withdrawals || []).map((w) => ({
    ...w,
    _pending: true,
    _kind: 'withdrawal',
    _rowKey: `pw-${w._id}`,
  }));
  return [...deposits, ...withdrawals];
});

// Unified list: pending rows first (so actions are immediately visible), then
// history. A sort applies across the whole set when the user clicks a header.
const rows = computed(() => {
  const history = displayTransactions.value.map((t, idx) => ({
    ...t,
    _pending: false,
    _rowKey: `h-${idx}-${t.createdAt}`,
  }));
  const combined = [...pendingRows.value, ...history];
  if (activeSort.value) return applySort(combined, activeSort.value);
  // Default: pending on top, then history newest-first.
  return combined;
});

onMounted(async () => {
  // try/finally so a failed fetch doesn't leave the spinner stuck forever.
  try {
    await Promise.all([userStore.getTransactions(), getDisplayTransactions()]);
  } catch (e) {
    console.error('Failed to load transactions', e);
  } finally {
    loading.value = false;
  }
});

async function getDisplayTransactions() {
  const response = await getTransactions({
    betPaginationCounter: betPaginationCounter.value,
  });
  displayTransactions.value = response.displayTransactions || [];
  loadMoreAvailable.value = response.allowNext;
}

async function loadMoreDisplay() {
  loading.value = true;
  betPaginationCounter.value.page += 1;
  try {
    const response = await getTransactions({
      betPaginationCounter: betPaginationCounter.value,
    });
    displayTransactions.value = displayTransactions.value.concat(
      response.displayTransactions || [],
    );
    loadMoreAvailable.value = response.allowNext;
  } catch (e) {
    console.error('Failed to load more transactions', e);
  } finally {
    loading.value = false;
  }
}

/* ---- Row display helpers ---- */
function rowLabel(t) {
  if (!t._pending) return t.message;
  if (t._kind === 'deposit') {
    const tok = (
      t.depositAddress?.token ||
      t.depositAddress?.blockchain ||
      ''
    ).toUpperCase();
    return tok ? `Deposit (${tok})` : 'Deposit';
  }
  const cur = t.currency ? t.currency.toUpperCase() : 'BITCOIN';
  return `Withdrawal (${cur})`;
}

// Single source of truth for a row's signed numeric value — used by the amount
// display, the color class, AND the $ sort so they never disagree. Pending
// withdrawals are negative (debits); deposits and history use their own sign.
function signedAmount(t) {
  const raw = t.amount?.$numberDecimal ?? t.amount;
  const num = Number(raw);
  if (isNaN(num)) return 0;
  if (t._pending) return t._kind === 'withdrawal' ? -num : num;
  return num;
}

function rowAmount(t) {
  return formatSigned(signedAmount(t));
}

function formatSigned(num) {
  const rounded = Math.round(num * 100) / 100;
  return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2);
}

function amountClass(t) {
  const num = signedAmount(t);
  return { positive: num > 0, negative: num < 0 };
}

function badgeText(t) {
  if (t._kind === 'withdrawal') return 'Processing';
  return t.userMarkedAsSent ? 'Awaiting Confirmation' : 'Awaiting Send';
}

function badgeClass(t) {
  if (t._kind === 'withdrawal') return 'wt-badge--pending';
  return t.userMarkedAsSent ? 'wt-badge--pending' : 'wt-badge--awaiting';
}

function displayTransaction(transaction) {
  selectedTransaction.value = transaction;
  transactionMemoModalShown.value = true;
}

/* ---- Sorting (pending rows kept on top within each sort) ---- */
function sortBy(key) {
  activeSort.value = key;
  sortDir.value[key] = (sortDir.value[key] + 1) % 2;
}

function applySort(list, key) {
  const dir = sortDir.value[key];
  const pending = list.filter((r) => r._pending);
  const history = list.filter((r) => !r._pending);
  const cmp = (a, b) => {
    let av;
    let bv;
    switch (key) {
      case 'time':
        av = new Date(a.createdAt);
        bv = new Date(b.createdAt);
        break;
      case 'type':
        av = rowLabel(a);
        bv = rowLabel(b);
        break;
      case 'dolar':
        av = signedAmount(a);
        bv = signedAmount(b);
        break;
      default:
        return 0;
    }
    if (av < bv) return dir === 0 ? -1 : 1;
    if (av > bv) return dir === 0 ? 1 : -1;
    return 0;
  };
  return [...pending.sort(cmp), ...history.sort(cmp)];
}

/* ---- Pending actions (preserved from PendingTransactions.vue) ---- */
async function cancelWithdrawal(idW) {
  const result = await Swal.fire({
    html: '<h4>Are you sure you want to cancel the withdrawal?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    confirmButtonColor: '#6f75b1',
  });
  if (!result.isConfirmed) return;
  loading.value = true;
  try {
    const res = await cancelWithdrawalRequest({ idWithdrawal: idW });
    await userStore.getTransactions();
    cancelWithdrawalRequestSuccess(res.message);
  } catch (error) {
    await userStore.getTransactions();
    cancelWithdrawalRequestFail(error);
  } finally {
    loading.value = false;
  }
}

async function cancelDeposit(depositRequestID) {
  const result = await Swal.fire({
    html: '<h4>Are you sure you want to cancel the deposit request?</h4>',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    confirmButtonColor: '#6f75b1',
  });
  if (!result.isConfirmed) return;
  loading.value = true;
  try {
    await cancelDepositRequest({ depositRequestID });
    await userStore.getTransactions();
    cancelDepositRequestSuccess();
  } catch (error) {
    cancelDepositRequestFail(error);
  } finally {
    loading.value = false;
  }
}

async function markAsSent(depositRequestID) {
  const result = await Swal.fire({
    html: '<h4>Did you send the deposit? If so, notify an admin by pressing submit.</h4>',
    showCancelButton: true,
    confirmButtonText: 'Submit',
    confirmButtonColor: '#6f75b1',
  });
  if (!result.isConfirmed) return;
  loading.value = true;
  try {
    await markAsSentRequest({ depositRequestID });
    // `source` tells the four components that fire this event apart — without
    // it the cashier surfaces are indistinguishable in analytics.
    PostHogUtils.capture('deposit_marked_sent', {
      source: 'wallet_transactions',
    });
    PostHogUtils.syncSettings({ has_ever_deposited: true });
    await userStore.getTransactions();
    markAsSentSuccess();
  } catch (error) {
    markAsSentFail(error);
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.wallet-transactions {
  max-width: 1200px;
}

.items-not-found {
  color: $grey-2400;
  background: $grey-500;
  font-size: $font-size-sm;
  border-radius: 10px;
}

.item {
  color: $black-203;
  border-radius: 10px;
  background: $grey-500;
}

// Header strip — match the legible My Bets column headers (light strip, blue
// text) so it stays readable on the dark My Bets background, on mobile too.
.wt-head {
  background: $white_01;
  color: $blue-1100;
  border-radius: 6px;
  box-shadow: 0 2px 4px 0 $blue-1200;

  @media screen and (max-width: 576px) {
    font-size: $font-size-xs;
  }
}

// Time cell: date on top, clock beneath — never wraps mid-date.
.wt-time {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;

  &__date {
    font-weight: 600;
    white-space: nowrap;
  }
  &__clock {
    font-size: $font-size-xs;
    opacity: 0.7;
    white-space: nowrap;
  }
}

.pointer {
  cursor: pointer;
}

.wt-row--pending {
  border-left: 3px solid $blue-1100;
}

.wt-type {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.wt-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;

  &--pending {
    background: rgba(255, 170, 30, 0.22);
    color: #a06a00;
  }
  &--awaiting {
    background: rgba(70, 120, 255, 0.16);
    color: #2e4fb0;
  }
}

.positive {
  color: $green-700;
}
.negative {
  color: $red-500;
}

.wt-actions {
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
}

.wt-action {
  padding: 5px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &--confirm {
    background: #2d8a4e;
    color: #fff;
    &:hover {
      background: #24753f;
    }
  }
  &--danger {
    background: rgba(220, 50, 50, 0.12);
    color: #c0392b;
    &:hover {
      background: rgba(220, 50, 50, 0.22);
    }
  }
}

.wt-detail-icon {
  cursor: pointer;
  color: $blue-1100;
}

/* ---- Mobile cards ---- */
.wt-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.wt-card {
  background: $grey-500;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;

  &--pending {
    border-left: 3px solid $blue-1100;
  }
}

.wt-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.wt-card__type {
  font-size: 14px;
  font-weight: 700;
  color: $black-203;
}

.wt-card__amount {
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
}

.wt-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

.wt-card__time {
  font-size: 12px;
  color: $black-203;
  opacity: 0.6;
}

.wt-card__actions {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

[theme='night'] {
  .wt-badge--pending {
    background: rgba(255, 180, 40, 0.18);
    color: #fbbf24;
  }
  .wt-badge--awaiting {
    background: rgba(100, 150, 255, 0.15);
    color: #93b4ff;
  }
  .wt-action--danger {
    background: rgba(220, 50, 50, 0.2);
    color: #f87171;
    &:hover {
      background: rgba(220, 50, 50, 0.3);
    }
  }
  .wt-card {
    background: $grey-500;
  }
  .wt-card__type,
  .wt-card__time {
    color: $color-primary_01;
  }
}
</style>
